/**
 * The board's client. CRM-BUILD item 28.
 *
 * ⚠️ THE SERVER'S OWN TYPES, IMPORTED RATHER THAN RESTATED, and the import is
 * `import type` on purpose: `booking-board.ts` imports the database driver, and
 * a VALUE import from it would take Postgres into the browser bundle. That is
 * not hypothetical - it happened on 2026-09-12 with `ROWS_PER_PAGE` and cost 127
 * modules. `ops/check-bundle-boundary.sh` now fails on it.
 */
import { reportUnauthorized } from './unauthorized.js'

export type { Board, BoardCard, BoardColumn } from '../../../src/services/booking-board.js'
import type { Board } from '../../../src/services/booking-board.js'
export type { BookingListPage, BookingListRow } from '../../../src/services/booking-list.js'
import type { BookingListPage } from '../../../src/services/booking-list.js'

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  return search.toString()
}

/** ⚠️ 401 REPORTS AND THROWS rather than returning a shape, which is every other
 *  fetcher here: a caller that forgot to check would render an empty board and
 *  say the account has no bookings. */
async function get<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as T
}

export async function fetchBoard(params: { view?: string; pipeline?: string }): Promise<Board> {
  return await get<Board>(`/api/bookings/board?${query(params)}`)
}

export async function fetchBookingList(params: {
  view?: string
  pipeline?: string
  sort?: string
  direction?: string
  q?: string
  page?: number
  limit?: number
}): Promise<BookingListPage> {
  return await get<BookingListPage>(`/api/bookings/list?${query(params)}`)
}

/**
 * ⛔ THE THREE OUTCOMES OF A MOVE, AS A UNION, because the caller renders each
 * one differently and two of them are NOT failures:
 *
 *   `moved`        the card is somewhere else now
 *   `unchanged`    dropped on its own column. Nothing was written, deliberately -
 *                  otherwise the activity feed fills with transitions that did
 *                  not happen. ⚠️ NOT an error, and the card does not snap back.
 *   `needs-confirm` the booking is leaving a lost stage with a reason attached.
 *                  ⛔ A QUESTION, NOT A REFUSAL. The reason travels with it so
 *                  the confirm can name what is about to be discarded.
 *   `failed`       anything else. The card snaps back.
 */
export type MoveOutcome =
  | { kind: 'moved' }
  | { kind: 'unchanged' }
  | { kind: 'needs-confirm'; lossReason: string | null }
  | { kind: 'failed'; message: string }

export async function moveBooking(
  bookingId: string,
  stageId: string,
  clearLossReason = false,
): Promise<MoveOutcome> {
  const res = await fetch(`/api/bookings/${bookingId}/stage`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ stageId, clearLossReason }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    return { kind: 'failed', message: 'Signed Out' }
  }
  if (res.status === 409) {
    const body = (await res.json()) as { lossReason?: string | null }
    return { kind: 'needs-confirm', lossReason: body.lossReason ?? null }
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    return { kind: 'failed', message: body.error ?? `HTTP ${res.status}` }
  }
  const body = (await res.json()) as { moved: boolean }
  return body.moved ? { kind: 'moved' } : { kind: 'unchanged' }
}

/** ⛔ Item 30's editor. There is no GET: the layout arrives on the board's own
 *  payload, because a second round trip for the same fact is two answers that
 *  can disagree mid-render. */
export async function saveCardLayout(input: {
  pipelineId: string | null
  slots: { key: string; visible: boolean }[]
  applyToAll: boolean
}): Promise<boolean> {
  const res = await fetch('/api/bookings/card-layout', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (res.status === 401) {
    reportUnauthorized()
    return false
  }
  return res.ok
}
